import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Button,
  Card,
  CardBody,
  Modal,
  ModalHeader,
  ModalBody,
} from "reactstrap";

import { useDispatch, useSelector } from "react-redux";
import { getAllLocation } from "../../slices/Location/GetLocation/thunk";
import { Loader } from "../Utils/Loader";
import CommonModal from "../Utils/CommonModal";
import { hasPermission } from "../Utils/UtilityFunctions";
import LocationForm from "../Location/LocationForm";
import { deleteLocation } from "../../slices/Location/DeleteLocation/thunk";

const StatusBadge = ({ status }) => {
  const isActive = status;

  return (
    <span
      className="d-inline-flex align-items-center justify-content-center gap-1 px-2"
      style={{
        minWidth: "95px",
        height: "28px",
        fontSize: "12px",
        fontWeight: 600,
        borderRadius: "6px",
        backgroundColor: isActive ? "#e9f9ef" : "#fde8e8",
        color: isActive ? "#1a7a45" : "#b72b2b",
        whiteSpace: "nowrap",
      }}
    >
      <i
        className={
          isActive ? "ri-checkbox-circle-fill" : "ri-close-circle-fill"
        }
        style={{ fontSize: "14px" }}
      />
      {isActive ? "Active" : "Inactive"}
    </span>
  );
};

function LocationCard() {
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();

  const [showModal, setShowModal] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);

  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteLocationData, setDeleteLocationData] = useState(null);
  const [messageForPopUp, setMessageForPopUp] = useState("");

  const { locations = [] } = useSelector((state) => state.locations);

  const rawPermissions = useSelector(
    (state) => state.permissions.permissionsMap,
  );

  const permissionsMap = new Map(
    rawPermissions.map(([key, value]) => [key, new Set(value)]),
  );

  const locationPermission = hasPermission(
    permissionsMap,
    "configuration",
    "location",
  );

  const toggleModal = () => setShowModal(!showModal);

  const toggleDeleteModal = () => {
    setDeleteModal(!deleteModal);
    document.body.style.overflow = "auto";
  };

  const fetchLocation = async () => {
    await dispatch(getAllLocation(setLoading));
  };

  useEffect(() => {
    fetchLocation();
  }, [dispatch]);

  const handleSuccess = async () => {
    await fetchLocation();
    setShowModal(false);
    setSelectedLocation(null);
  };

  const handleAddClick = () => {
    setSelectedLocation(null);
    setShowModal(true);
  };

  const handleCardClick = (loc) => {
    setSelectedLocation(loc);
    setShowModal(true);
  };

  const handleDelete = (e, loc) => {
    e.stopPropagation();
    setDeleteLocationData(loc);
    setMessageForPopUp(
      `Are you sure you want to delete location "${loc?.name}" ?`,
    );
    toggleDeleteModal();
  };

  return (
    <React.Fragment>
      {loading && <Loader />}
      <Row>
        <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between w-100 px-3">
          <h5 className="rv-page-title p-1">Warehouse Management</h5>

          {locationPermission?.has("create") && (
            <Button
              className="btn-label rv-primary-btn rounded-1 px-1 ps-4 py-1"
              onClick={handleAddClick}
            >
              <i className="ri-add-fill label-icon fs-16 me-1 fw-bold"></i>
              <span className="ps-2 pe-2 fw-bold">Add Location</span>
            </Button>
          )}
        </div>
      </Row>

      {/* CARDS */}
      <Row className="px-2 mt-2">
        {Array.isArray(locations) &&
          locations.map((loc) => (
            <Col
              key={loc.locationid}
              xs="12"
              sm="6"
              lg="4"
              xl="4"
              className="mb-3"
            >
              <Card
                className="border"
                style={{
                  borderColor: "#DFDFDF",
                  borderRadius: "8px",
                }}
              >
                <CardBody className="p-3">
                  <div className="d-flex justify-content-between align-items-start mb-2 gap-4">
                    <div className="d-flex gap-2 align-items-center">
                      <div
                        className="d-flex align-items-center justify-content-center flex-shrink-0 rounded"
                        style={{
                          width: "42px",
                          height: "42px",
                          backgroundColor: "#FFF0EA",
                        }}
                      >
                        <i
                          className="ri-map-pin-2-line"
                          style={{ color: "#FD7545", fontSize: "18px" }}
                        />
                      </div>

                      {/* TEXT */}
                      <div>
                        <span
                          className="mb-0 fw-semibold"
                          style={{ fontSize: "15px" }}
                        >
                          {loc.name}
                        </span>

                        <p
                          className="mb-0 text-muted"
                          style={{
                            fontSize: "12px",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {loc.address?.line1}, {loc.address?.city},{" "}
                          {loc.address?.state}, {loc.address?.country} -{" "}
                          {loc.address?.pincode}
                        </p>
                      </div>
                    </div>

                    <div>
                      <div className="d-flex justify-content-end gap-1 mb-2">
                        {locationPermission?.has("update") && (
                          <div
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCardClick(loc);
                            }}
                            className="d-flex align-items-center justify-content-center rounded"
                            style={{
                              cursor: "pointer",
                            }}
                          >
                            <i
                              className="ri-edit-box-line fs-4 text-dark cursor-pointer"
                              role="button"
                            ></i>
                          </div>
                        )}

                        {locationPermission?.has("delete") && (
                          <div
                            onClick={(e) => handleDelete(e, loc)}
                            className="d-flex align-items-center justify-content-center rounded"
                            style={{
                              cursor: "pointer",
                            }}
                          >
                            <i
                              className="ri-delete-bin-line fs-4 text-danger cursor-pointer"
                              role="button"
                            ></i>
                          </div>
                        )}
                      </div>
                      <StatusBadge status={loc.isActive} />
                    </div>
                  </div>

                  {/* DETAILS */}
                  {/* <div className="d-flex justify-content-between small mt-2">
                    <span>Code</span>
                    <span className="fw-semibold">{loc.code}</span>
                  </div>

                  <div className="d-flex justify-content-between small mt-2">
                    <span>Type</span>
                    <span className="fw-semibold text-capitalize">
                      {loc.type}
                    </span>
                  </div>

                  <div className="d-flex justify-content-between small mt-2">
                    <span>Items</span>
                    <span className="fw-semibold">
                      {Array.isArray(loc.itemdetails)
                        ? `${loc.itemdetails.length} Items / ${loc.itemdetails.reduce(
                            (total, item) => total + Object.values(item)[0],
                            0,
                          )} Qty`
                        : "0"}
                    </span>
                  </div> */}

                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex gap-2 small mt-2">
                      <span>Code</span>-
                      <span className="fw-semibold">{loc.code}</span>
                    </div>

                    <div className="d-flex gap-2 small mt-2">
                      <span>Items</span>-
                      <span className="fw-semibold">
                        {Array.isArray(loc.itemdetails)
                          ? `${loc.itemdetails.length} Items / ${loc.itemdetails.reduce(
                              (total, item) => total + Object.values(item)[0],
                              0,
                            )} Qty`
                          : "0"}
                      </span>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          ))}
      </Row>

      {/* ADD / EDIT MODAL */}
      <Modal isOpen={showModal} toggle={toggleModal} centered size="md">
        <ModalHeader
          toggle={toggleModal}
          style={{
            borderBottom: "1px solid #FD7545",
            padding: "16px 20px",
          }}
        >
          <span style={{ color: "#FD7545", fontWeight: 600 }}>
            {selectedLocation ? "Edit Location" : "Add New Location"}
          </span>
        </ModalHeader>

        <ModalBody style={{padding: "20px 20px 0px 20px" }}>
          <LocationForm
            closeModal={toggleModal}
            onSuccess={handleSuccess}
            selectedLocation={selectedLocation}
          />
        </ModalBody>
      </Modal>

      {/* DELETE MODAL */}
      <CommonModal
        open={deleteModal}
        toggle={toggleDeleteModal}
        buttonText={"Yes, Delete It"}
        messageForPopUp={messageForPopUp}
        modalHeader={"Delete Location"}
        modalAction={() => {
          dispatch(deleteLocation(deleteLocationData?.locationid, setLoading));
          toggleDeleteModal();
        }}
      />
    </React.Fragment>
  );
}

export default LocationCard;
